import { RecordBalanceForm, SnapshotHistory } from "@gladis/domain-savings/ui";
import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

/**
 * One savings account and its history.
 *
 * THE TRAILING UNDERSCORE ON `savings_` IS LOAD-BEARING.
 *
 * Without it TanStack Router treats this as a CHILD of `/savings`, which would require
 * `savings.tsx` to render an `<Outlet/>` and would draw this page inside the accounts list rather
 * than instead of it. The underscore opts out of that nesting while keeping the file flat beside
 * its siblings, which is how every other route in this directory is laid out.
 *
 * It resolves on a direct visit, not only on navigation — feature 012's activity feed links here,
 * and a target that only works if you arrive from somewhere else is not a target.
 */
export const Route = createFileRoute("/_authenticated/_shell/savings_/$accountId")({
  component: SavingsAccount,
});

function SavingsAccount(): ReactNode {
  const t = useT();
  const { accountId } = Route.useParams();
  const [updating, setUpdating] = useState(false);

  return (
    <div className="mx-auto w-full max-w-content px-6 py-10">
      <Link to="/savings" className="text-sm underline">
        ← All savings
      </Link>

      {/*
        RECORDING A BALANCE IS A MODAL HERE TOO, and the button is what replaced the panel that used to
        sit permanently under the history. The same form on the Savings page is opened from a row's
        action menu; this page has one account, so its trigger is a plain button beside the history.

        `aria-haspopup="dialog"` and NOT `aria-expanded`: the dialog has three ways to close that this
        control is not one of, so it opens rather than toggles. It carries no account NAME — the history
        below is already headed with it, and the dialog would be saying it a second time.
      */}
      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          aria-haspopup="dialog"
          onClick={() => {
            setUpdating(true);
          }}
        >
          {t("savings.list.updateBalance")}
        </Button>
      </div>

      <div className="mt-4">
        <SnapshotHistory accountId={accountId} />
      </div>

      {/* Always mounted; `open` decides whether it is seen. See the note on `AccountForm` in
          `savings.tsx` for why a native `<dialog>` cannot be rendered conditionally. */}
      <RecordBalanceForm
        open={updating}
        onClose={() => setUpdating(false)}
        accountId={accountId}
        onRecorded={() => setUpdating(false)}
      />
    </div>
  );
}
